import React, { useEffect, useState } from "react";
import { Drawer, Box, Typography, IconButton, Button, CircularProgress } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import { CompositionTree, TraceSteps } from "./ArticleTraceView";
import { GapActionRow, useGapResolution, sortPendingFirst, pendingCountOf } from "./gapResolution";

// Material ficha — two tabs like the article ficha: Pendientes (its gaps + inline actions) and
// Trazabilidad (facts + composition + finishing). Gaps pre-loaded; trace from
// GET /articlesTrace/material/:id/trace. Actions resolve every underlying gap of the field.
const useStyles = makeStyles((theme) => ({
  drawer: { width: "50vw", minWidth: 620, maxWidth: 940 },
  facts: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 4, lineHeight: 1.8 },
  tabs: { display: "flex", gap: 8, padding: "0 16px 8px" },
  body: { padding: theme.spacing(0, 2, 3) },
  sub: { fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.palette.text.secondary, margin: "14px 0 6px" },
  empty: { color: "#94a3b8", padding: theme.spacing(1, 0) },
}));
const muted = { color: "#94a3b8" };

const MaterialTracePanel = ({ api, materialId, materialName, gaps = [], contracted, initialTab = "pendientes", onClose, onChanged }) => {
  const classes = useStyles();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(initialTab);
  const { rows, busyId, provide, assume, reject } = useGapResolution(api, gaps, materialId, onChanged);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const r = await api("GET", `/articlesTrace/material/${materialId}/trace`);
      if (!alive) return;
      setD(r.ok ? r.data : null);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [api, materialId]);

  const sortedGaps = sortPendingFirst(rows);
  const pendingCount = pendingCountOf(rows);

  return (
    <Drawer anchor="right" open onClose={onClose} classes={{ paper: classes.drawer }}>
      <Box p={2} pb={1}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="h6" style={{ lineHeight: 1.2 }}>{d?.name || materialName || "Material"}</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        {d && (
          <div className={classes.facts}>
            <span style={muted}>Categoría: </span>{d.category ?? "—"} {"  ·  "}
            <span style={muted}>Aplicación: </span>{d.application ?? "—"} {"  ·  "}
            <span style={muted}>Origen: </span>{d.origin ?? "—"} {"  ·  "}
            <span style={muted}>Merma: </span>{d.lossRate ?? "—"}
          </div>
        )}
      </Box>

      <Box className={classes.tabs}>
        <Button size="small" variant={tab === "pendientes" ? "contained" : "outlined"} color="primary" onClick={() => setTab("pendientes")}>
          Pendientes{pendingCount ? ` (${pendingCount})` : ""}
        </Button>
        <Button size="small" variant={tab === "trace" ? "contained" : "outlined"} color="primary" onClick={() => setTab("trace")}>
          Trazabilidad
        </Button>
      </Box>

      <Box className={classes.body}>
        {tab === "pendientes" &&
          (sortedGaps.length === 0 ? (
            <div className={classes.empty}>Sin pendientes en este material.</div>
          ) : (
            sortedGaps.map((g) => (
              <GapActionRow
                key={g.id}
                gap={g}
                contracted={contracted}
                busy={busyId === g.id}
                onProvide={provide}
                onAssume={assume}
                onReject={reject}
              />
            ))
          ))}

        {tab === "trace" && (
          <>
            {loading && (
              <Box textAlign="center" py={3}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!loading && !d && <Box style={muted} mt={2}>Sin trazabilidad</Box>}
            {!loading && d && (
              <>
                <div className={classes.sub}>Composición (extracción → procesado)</div>
                <CompositionTree extractions={d.extractions} processings={d.processings} />
                <div className={classes.sub}>Acabado</div>
                <TraceSteps steps={d.finishings} showType />
              </>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default MaterialTracePanel;
